import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconMailOff,
  IconSparkles,
  IconPointer,
  IconShieldCheck,
  IconClock,
  IconTrendingDown,
  IconCheck,
  IconArrowRight
} from '@tabler/icons-react'

export const Route = createFileRoute('/mass-unsubscribe')({
  component: MassUnsubscribePage,
})

function MassUnsubscribePage() {
  const benefits = [
    { icon: <IconClock className="w-5 h-5" />, text: "Save hours of manual work" },
    { icon: <IconShieldCheck className="w-5 h-5" />, text: "Privacy-first approach" },
    { icon: <IconTrendingDown className="w-5 h-5" />, text: "Reduce inbox clutter by 70%" },
  ]

  const methods = [
    {
      title: "Manual Method",
      description: "Search for 'unsubscribe' and click each link individually",
      time: "2-3 hours",
      recommended: false
    },
    {
      title: "Gmail Filters",
      description: "Create filters to auto-delete unwanted emails",
      time: "30 minutes setup",
      recommended: false
    },
    {
      title: "AI-Powered (Winbox Zero)",
      description: "Automatically identify and batch unsubscribe from unwanted senders",
      time: "5 minutes",
      recommended: true
    }
  ]

  const features = [
    "Automatically detects newsletter and promotional emails",
    "Groups emails by sender for easy bulk actions",
    "Handles various unsubscribe methods (links, headers, mailto)",
    "Tracks your progress toward inbox zero"
  ]

  return (
    <div className="bg-[#fafafa] text-slate-950 min-h-screen">
      <StaticNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-red-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-red-50 border border-red-100">
              <IconMailOff className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                Stop the email flood
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Mass Unsubscribe from{' '}
              <span className="text-red-600">Unwanted Emails</span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              The average person receives over 100 emails per day. Learn how to bulk
              unsubscribe and reclaim your inbox once and for all.
            </p>

            <div className="flex flex-wrap justify-center gap-6 mb-8">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-600">
                  <div className="text-blue-600">{benefit.icon}</div>
                  <span className="text-sm font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Methods Comparison */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            How to Mass Unsubscribe
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Compare different approaches to bulk unsubscribe from emails
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {methods.map((method, i) => (
              <div
                key={i}
                className={`relative p-6 rounded-3xl border-2 transition-all ${
                  method.recommended
                    ? 'border-blue-500 bg-blue-50/50 shadow-lg'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                {method.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                    Recommended
                  </div>
                )}
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {method.title}
                </h3>
                <p className="text-slate-600 mb-4 text-sm">
                  {method.description}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <IconClock className="w-4 h-4 text-slate-400" />
                  <span className={method.recommended ? 'text-blue-600 font-medium' : 'text-slate-500'}>
                    {method.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Winbox Zero Features */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                <IconSparkles className="w-4 h-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                The Winbox Zero Approach
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Our AI analyzes your inbox to identify emails you likely want to
                unsubscribe from. Intelligent categorization makes bulk unsubscribing effortless.
              </p>
              <ul className="space-y-3">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <IconCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl p-8">
              <div className="bg-white rounded-3xl shadow-lg p-6 space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                  <IconMailOff className="w-5 h-5 text-red-500" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Newsletter Weekly</div>
                    <div className="text-xs text-slate-500">47 emails from this sender</div>
                  </div>
                  <IconPointer className="w-4 h-4 text-red-500" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                  <IconMailOff className="w-5 h-5 text-red-500" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Daily Deals</div>
                    <div className="text-xs text-slate-500">123 emails from this sender</div>
                  </div>
                  <IconPointer className="w-4 h-4 text-red-500" />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                  <IconMailOff className="w-5 h-5 text-red-500" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">Promo Alerts</div>
                    <div className="text-xs text-slate-500">89 emails from this sender</div>
                  </div>
                  <IconPointer className="w-4 h-4 text-red-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Best Practices for Staying Unsubscribed
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Tips to keep your inbox clean after the initial cleanup
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Use a separate email", description: "Create a dedicated email for signups and promotions" },
              { title: "Think before subscribing", description: "Consider if you'll actually read the newsletter" },
              { title: "Regular cleanup sessions", description: "Schedule weekly inbox reviews to stay on top" },
              { title: "Use automation tools", description: "Let AI maintain your clean inbox automatically" }
            ].map((tip, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{tip.title}</h3>
                  <p className="text-sm text-slate-600">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-20 shadow-2xl sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl mix-blend-screen" />
            <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl mix-blend-screen" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Ready to mass unsubscribe?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Get started today with unlimited email processing.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}
